import type { ApiRequestHelpers } from "./useHttpClient";
import type { AssignableEmployee, CollectionResponse, Response, Todo, TodosCountData } from "~/types/api";

export type TodoScope = "assigned_to_me" | "created_by_me_for_others";

export function createTodoApi({ getRequest, postRequest, putRequest, patchRequest, deleteRequest }: ApiRequestHelpers) {
  /** Fetches todos for the current user, filtered by scope. */
  const todos = (scope: TodoScope = "assigned_to_me") =>
    getRequest<CollectionResponse<Todo>>(`/todos`, { params: { scope } });

  /** Fetches the count of open todos. */
  const todosCount = () => getRequest<Response<TodosCountData>>("/todos/count");

  /** Creates a new todo item. */
  const createTodo = (data: {
    title: string;
    deadline: string;
    patient_id?: number;
    assignee_practitioner_id: string;
    assignee_practitioner_role_id?: string | null;
    description?: string;
  }) => postRequest<Response<Todo>>("/todos", data);

  /** Fetches employees that can be assigned to a todo. */
  const assignableEmployees = () => getRequest<CollectionResponse<AssignableEmployee>>("/todos/assignable-employees");

  /** Toggles the completion status of a todo. */
  const markTodoComplete = (todoId: number, is_completed: boolean) =>
    patchRequest<Response<Todo>>(`/todos/${todoId}/complete`, { is_completed });

  /** Permanently deletes a todo. */
  const deleteTodo = (todoId: number) => deleteRequest<void>(`/todos/${todoId}`);

  /** Updates the title, deadline, assignee, patient, and description of an existing todo. */
  const updateTodo = (
    todoId: number,
    data: {
      title: string;
      deadline: string;
      assignee_practitioner_id: string;
      assignee_practitioner_role_id?: string | null;
      patient_id?: number;
      description?: string;
    },
  ) => putRequest<Response<Todo>>(`/todos/${todoId}`, data);

  return { todos, todosCount, createTodo, assignableEmployees, markTodoComplete, deleteTodo, updateTodo };
}

export type TodoApi = ReturnType<typeof createTodoApi>;
